import type { Mark as PMMark } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, SafePluginSpec } from '@atlaskit/editor-prosemirror/state';
import { NodeSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { closeHistory } from '@atlaskit/prosemirror-history/closeHistory';

import { isGapCursorSelection } from './editor-common';
import type { InputRuleWrapper } from './editor-common';
import { createInputRulePlugin } from './plugin';
import type { OnInputEvent } from './types';

const hasUnsupportedMarks = (
	state: EditorState,
	start: number,
	end: number,
	marksNameUnsupported: string[],
) => {
	const isUnsupportedMark = (node: PMMark) => (marksNameUnsupported || []).includes(node.type.name);

	const $from = state.doc.resolve(start);
	const $to = state.doc.resolve(end);
	const marksInSelection = start === end ? $from.marks() : $from.marksAcross($to);

	return (marksInSelection || []).some(isUnsupportedMark);
};

const isCursorInsideUnsupportedMarks = (
	state: EditorState,
	marksNameUnsupported: string[],
): boolean => {
	const { selection } = state;
	if (!(selection instanceof TextSelection)) {
		return false;
	}
	const { $cursor } = selection;
	const isUnsupportedMark = (node: PMMark) => marksNameUnsupported.includes(node.type.name);

	return Boolean($cursor?.nodeBefore?.marks?.some(isUnsupportedMark));
};

type Options = {
	allowInsertTextOnDocument?: boolean;
	/**
	 * Append text to the checked text on blur. Useful for checks that normally require a trailing
	 * space or similar
	 */
	appendTextOnBlur?: string;
	/**
	 * Run checks on blur of the editor as well as during input
	 */
	checkOnBlur?: boolean;
	isBlockNodeRule?: boolean;
};
export const createPlugin = (
	pluginName: string,
	rules: Array<InputRuleWrapper>,
	options: Options = {},
): SafePluginSpec => {
	const {
		isBlockNodeRule = false,
		allowInsertTextOnDocument = true,
		checkOnBlur,
		appendTextOnBlur,
	} = options;

	const onInputEvent: OnInputEvent = ({ state, from, to }) => {
		const unsupportedMarks = isBlockNodeRule ? ['code', 'link', 'typeAheadQuery'] : ['code'];

		const $from = state.selection.$from;
		const isInline = state.selection instanceof NodeSelection && state.selection.node.type.isInline;

		if (
			$from.parent.type.spec.code ||
			(!(state.selection instanceof TextSelection) &&
				!isGapCursorSelection(state.selection) &&
				!isInline) ||
			hasUnsupportedMarks(state, from, to, unsupportedMarks) ||
			(isBlockNodeRule && isCursorInsideUnsupportedMarks(state, unsupportedMarks))
		) {
			return false;
		}

		return true;
	};

	return createInputRulePlugin(pluginName, rules, {
		allowInsertTextOnDocument,
		onInputEvent,
		onBeforeRegexMatch: (tr) => {
			closeHistory(tr);
		},
		checkOnBlur,
		appendTextOnBlur,
	});
};
